import { Injectable } from '@angular/core';
import { ViewBy } from '@portal/app/shared/constants';
import { MetricDTO } from '@libs/api';
import {
  ChartDataPoint,
  ChartScatterDataPoint
} from '@portal/app/mpo/shared/types/chart.types';
import { colors } from '@design-system/styles/colors';
import dayjs from 'dayjs';

interface ReturnCurveDataPoint {
  mediaSpend: MetricDTO;
  ordersLT?: MetricDTO;
  salesLT?: MetricDTO;
  incrementalOrders?: MetricDTO;
  incrementalCPO?: MetricDTO;
  incrementalSales?: MetricDTO;
  incrementalROAS?: MetricDTO;
  marginalROAS?: MetricDTO;
  marginalCPO?: MetricDTO;
}
interface DatedReturnCurveDataPoint extends ReturnCurveDataPoint {
  year: number;
  week: number;
  date?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ReturnCurveChartSeriesHelper {
  static metricMap = {
    ROAS: {
      platformMetric: { field: 'salesLT', label: 'Platform Sales' },
      metricKey2: { field: 'incrementalSales', label: 'Incremental Sales' },
      metricKey3: { field: 'incrementalROAS', label: 'Incremental ROAS' },
      metricKey4: { field: 'marginalROAS', label: 'Marginal ROAS' }
    },
    CPO: {
      platformMetric: { field: 'ordersLT', label: 'Platform Orders' },
      metricKey2: { field: 'incrementalOrders', label: 'Incremental Orders' },
      metricKey3: { field: 'incrementalCPO', label: 'Incremental CPO' },
      metricKey4: { field: 'marginalCPO', label: 'Marginal CPO' }
    }
  };

  static createScatterPlotData(
    data: DatedReturnCurveDataPoint[],
    viewByMode: ViewBy
  ): ChartScatterDataPoint[] {
    const sortedDataByYearAndWeekInDesc =
      ReturnCurveChartSeriesHelper.sortDataByYearAndWeekInDesc(data);

    const dataWithoutLast4Weeks = [...sortedDataByYearAndWeekInDesc].slice(
      0,
      -4
    );
    const last4WeeksData =
      ReturnCurveChartSeriesHelper.filterInValidPlatformData(
        [...[...sortedDataByYearAndWeekInDesc].slice(-4)],
        viewByMode
      );

    // Filter out points where salesLT or ordersLT is 0
    const validData = ReturnCurveChartSeriesHelper.filterInValidPlatformData(
      dataWithoutLast4Weeks,
      viewByMode
    );

    // Sort by salesLT or ordersLT descending to filter outliers based on view mode
    const sortedDataDesc =
      ReturnCurveChartSeriesHelper.sortDataByPlatformMetricInDesc(
        [...validData],
        viewByMode
      );

    // Filter out top 2% to avoid outliers
    const OUTLIER_PERC = 0.02;
    const topPercentCount = Math.floor(sortedDataDesc.length * OUTLIER_PERC);
    const filteredOutlierData = sortedDataDesc.slice(topPercentCount);

    const outlierDataSortedByDate =
      ReturnCurveChartSeriesHelper.sortDataByYearAndWeekInDesc([
        ...filteredOutlierData,
        ...last4WeeksData
      ]);

    // Create scatter plot data points [spend, salesLT/ordersLT, year, week]
    return ReturnCurveChartSeriesHelper.createScatterPlotDataPoints(
      outlierDataSortedByDate,
      viewByMode
    );
  }

  private static filterInValidPlatformData(
    data: DatedReturnCurveDataPoint[],
    viewByMode: ViewBy
  ) {
    const metricKey1 = this.metricMap[viewByMode].platformMetric.field;

    return data.filter((item) => {
      const dataObj = item[metricKey1 as keyof ReturnCurveDataPoint];

      const yValue = (dataObj?.value ?? 0) as number;

      return yValue > 0;
    });
  }

  static sortDataByPlatformMetricInDesc(
    validData: DatedReturnCurveDataPoint[],
    viewByMode: ViewBy
  ) {
    return validData.sort((a, b) => {
      const metricKey1 = this.metricMap[viewByMode].platformMetric.field;

      const aData = a[metricKey1 as keyof ReturnCurveDataPoint];
      const bData = b[metricKey1 as keyof ReturnCurveDataPoint];

      const aValue = (aData?.value ?? 0) as number;
      const bValue = (bData?.value ?? 0) as number;

      return bValue - aValue;
    });
  }

  private static sortDataByYearAndWeekInDesc(
    data: DatedReturnCurveDataPoint[]
  ) {
    return data.sort((a, b) => {
      const aYear = a.year || 0;
      const bYear = b.year || 0;
      const aWeek = a.week || 0;
      const bWeek = b.week || 0;

      if (aYear === bYear) {
        return aWeek - bWeek; // If years are the same, sort by week
      }
      return aYear - bYear; // Otherwise, sort by year
    });
  }

  private static createScatterPlotDataPoints(
    outlierDataSortedByDate: DatedReturnCurveDataPoint[],
    viewByMode: ViewBy
  ): ChartScatterDataPoint[] {
    const platformMetric = this.metricMap[viewByMode].platformMetric.field;

    return outlierDataSortedByDate.map((item) => {
      const spend = (item.mediaSpend?.value ?? 0) as number;

      const yValue = (item[platformMetric as keyof ReturnCurveDataPoint]
        ?.value ?? 0) as number;

      return {
        x: spend,
        y: yValue,
        xFormatted: item.mediaSpend?.formattedValue ?? '',
        yFormatted:
          item[platformMetric as keyof ReturnCurveDataPoint]?.formattedValue ??
          '',
        weekStartDate: item.date,
        year: item.year || 0,
        week: item.week || 0
      } as ChartScatterDataPoint;
    });
  }

  static sortDataBySpendInAsc<T extends ReturnCurveDataPoint>(data: T[]) {
    return [...data].sort((a, b) => {
      const aSpend = (a.mediaSpend?.value ?? 0) as number;
      const bSpend = (b.mediaSpend?.value ?? 0) as number;
      return aSpend - bSpend;
    });
  }

  static preparePrimarySeriesData(
    spendAscBundledData: ReturnCurveDataPoint[],
    viewBy: ViewBy
  ) {
    const metricKey2 = this.metricMap[viewBy].metricKey2.field;

    return spendAscBundledData.map((item) => {
      const primaryCurveObj = item[metricKey2 as keyof ReturnCurveDataPoint];

      return {
        x: (item.mediaSpend.value || 0) as number,
        y: (primaryCurveObj?.value || 0) as number,
        xFormatted: item.mediaSpend.formattedValue ?? '',
        yFormatted: primaryCurveObj?.formattedValue ?? ''
      };
    });
  }

  static prepareSecondarySeriesData(
    spendAscBundledData: ReturnCurveDataPoint[],
    viewBy: ViewBy
  ) {
    const metricKey3 = this.metricMap[viewBy].metricKey3.field;

    return spendAscBundledData.map((item) => {
      const platformCurveObj = item[metricKey3 as keyof ReturnCurveDataPoint];
      return {
        x: (item.mediaSpend.value || 0) as number,
        y: (platformCurveObj?.value || 0) as number,
        xFormatted: item.mediaSpend.formattedValue ?? '',
        yFormatted: platformCurveObj?.formattedValue ?? ''
      };
    });
  }

  static prepareTertiarySeriesData(
    spendAscBundledData: ReturnCurveDataPoint[],
    viewBy: ViewBy
  ) {
    const metricKey4 = this.metricMap[viewBy].metricKey4.field;

    return spendAscBundledData
      .map((item) => {
        const platformCurveObj = item[metricKey4 as keyof ReturnCurveDataPoint];

        // Compatibility if metric is not available
        if (!platformCurveObj) {
          return null;
        }

        return {
          x: (item.mediaSpend.value || 0) as number,
          y: (platformCurveObj.value || 0) as number,
          xFormatted: item.mediaSpend.formattedValue ?? '',
          yFormatted: platformCurveObj.formattedValue ?? ''
        };
      })
      .filter(Boolean) as ChartDataPoint[];
  }

  static getYAxisMaxValues(
    primaryData: ChartDataPoint[],
    secondaryData: ChartDataPoint[],
    scatterPlotData: ChartScatterDataPoint[]
  ) {
    const primaryValues = primaryData
      .map((item) => item.y)
      .filter((item) => Number.isFinite(item)) as number[];

    const secondaryValues = secondaryData
      .map((item) => item.y)
      .filter((item) => Number.isFinite(item)) as number[];

    const CHART_HEIGHT_SCALER = 1.0;
    const scatterPointMax = Math.max(
      ...scatterPlotData.map((item) => item.y as number)
    );
    const primaryMax =
      Math.max(...[scatterPointMax, ...primaryValues]) * CHART_HEIGHT_SCALER;
    const secondaryMax = Math.max(...secondaryValues) * CHART_HEIGHT_SCALER;

    return { primaryMax, secondaryMax };
  }

  static getXAxisMaxValue(data: ReturnCurveDataPoint[]) {
    const spends = data.map((item) => (item.mediaSpend.value || 0) as number);
    return Math.max(...spends);
  }

  static getPrimarySeries(primaryData: ChartDataPoint[], viewBy: ViewBy) {
    return {
      data: primaryData,
      name: this.metricMap[viewBy].metricKey2.label,
      type: 'spline',
      yAxis: 0,
      color: colors['green-600'],
      marker: { enabled: false },
      dataLabels: { enabled: false }
    };
  }

  static getSecondarySeries(secondaryData: ChartDataPoint[], viewBy: ViewBy) {
    return {
      data: secondaryData,
      name: this.metricMap[viewBy].metricKey3.label,
      type: 'spline',
      yAxis: 1,
      color: colors['orange-200'],
      marker: { enabled: false },
      dataLabels: { enabled: false }
    };
  }

  static getTertiarySeries(tertiaryData: ChartDataPoint[], viewBy: ViewBy) {
    return {
      name: this.metricMap[viewBy].metricKey4.label,
      type: 'line',
      data: tertiaryData,
      showInLegend: false,
      marker: { enabled: false },
      color: '#00000000',
      lineWidth: 0,
      opacity: 0,
      yAxis: 1
    };
  }

  static getScatterPlotSeries(
    scatterPlotData: ChartScatterDataPoint[],
    viewByMode: ViewBy
  ) {
    return {
      name: 'Historical Data',
      type: 'scatter',
      showInLegend: false,
      lineWidth: 0,
      data: scatterPlotData.map((item) => ({
        x: item.x,
        y: item.y,
        xFormatted: item.xFormatted,
        yFormatted: item.yFormatted,
        seriesName:
          viewByMode === ViewBy.roas ? 'Platform Sales' : 'Platform Orders',
        weekStartDate: dayjs(item.weekStartDate).utc().format('MMM DD, YYYY'),
        custom: {
          year: item.year,
          week: item.week
        }
      })),
      color: colors['blue-200'],
      marker: {
        symbol: 'circle',
        radius: 3
      }
    };
  }

  static getLatestFourScatterPlotSeries(
    scatterPlotData: ChartScatterDataPoint[],
    viewByMode: ViewBy
  ) {
    return {
      name: `${viewByMode === ViewBy.roas ? 'Platform Sales vs Spend (Last 4 weeks)' : 'Platform Orders vs Spend (Last 4 weeks)'}`,
      type: 'scatter',
      lineWidth: 0,
      data: (scatterPlotData || []).slice(-4).map((item) => ({
        x: item.x,
        y: item.y,
        xFormatted: item.xFormatted,
        yFormatted: item.yFormatted,
        weekStartDate: dayjs(item.weekStartDate).utc().format('MMM DD, YYYY'),
        seriesName:
          viewByMode === ViewBy.roas ? 'Platform Sales' : 'Platform Orders',
        custom: {
          year: item.year,
          week: item.week
        }
      })),
      color: colors['blue-400'],
      marker: {
        symbol: 'circle',
        radius: 4
      }
    };
  }
}
